// This plugin's own state: the account to import for, which of its months
// are in, and where an import stands. The month list and every fetched
// response live in the plugin's website-keyed folder,
// `archives/chesscom/<account>/`, and nowhere near the games API. Nothing
// here calls chess.com except the import, from a click or from the daily
// sync (`@chess/api-sync`, which index.tsx provides over this store). The
// import covers the last 30 days only, the app's one window
// (`@chess/api-games/filter`): the months that hold them, and of their games
// only those played in the window. When it completes, the local day is
// written to the bookkeeping (`lastSyncDay`): the daily sync is due while
// that is not today.
import { create } from 'zustand';
import type { Storage } from '@chess/api-storage';
import type { Games } from '@chess/api-games';
import { inPeriod, periodStart } from '@chess/api-model';
import { localDay } from '@chess/api-sync';
import type { Toast } from '@chess/api-toast';
import { monthOf, toGameRecord } from './chesscom.ts';
import {
  ArchiveStore, IMPORT_CLICK_INTERVAL_MS, LIVE_MONTH_INTERVAL_MS, REFRESH_INTERVAL_MS, ageMs, consoleLog, currentMonth, loadArchives, loadMonth, monthsSince, readUsername,
  type CallLog,
} from './import.ts';

export type Status = 'loading' | 'idle' | 'importing' | 'done' | 'error';

export const SETTINGS_FILE = 'chesscom.json';

/** The storage folder every path of this importer lives under. */
export const ARCHIVES_FOLDER = 'archives/chesscom';

export interface ImportProgress {
  /** The month being read, "2026/10". */
  month: string;
  imported: number;
  skipped: number;
}

export interface Deps {
  games: Games;
  /** The importer's website-keyed folder, `archives/chesscom`. */
  storage: Storage;
  fetch?: typeof fetch;
  now?: () => Date;
  log?: CallLog;
  /** Where a refused click is explained; optional so the store runs without a UI. */
  toast?: Toast;
}

interface State {
  /** '' when no account is configured. */
  username: string;
  status: Status;
  progress?: ImportProgress;
  error?: string;
  /** The stored archive list, oldest first; undefined until chess.com has been asked once. */
  archives?: string[];
  /** Archive URLs whose games are in the library. */
  imported: string[];
  /** A sentence for a `done` that imported nothing. */
  note?: string;
  /** The local day the last import completed, from the bookkeeping; undefined when none ever did for this account. */
  lastSyncDay?: string;
  /** When the last import completed, ISO 8601, from the bookkeeping. */
  lastImportAt?: string;
  /** The last click on Sync, ISO 8601, from the bookkeeping: the button waits a minute after it (`coolingMs`). */
  importClickedAt?: string;
  /** The newest game the last import brought in: the one the reader most likely came to see; the control links to its review once it is scanned. */
  latest?: { id: string; opponent: string };
  load(settings: Storage, archives: Storage): Promise<void>;
  /** The import. `auto` is the daily sync's: not a click, so the one-click-a-minute rule does not apply. */
  importRecent(deps: Deps, options?: { auto?: boolean }): Promise<void>;
}

export const useImport = create<State>((set, get) => {
  // One import at a time: `status` is the lock, and each request inside is awaited in turn.
  async function run(deps: Deps, auto: boolean): Promise<void> {
    const { username, status } = get();
    if (!username || status === 'importing') return;
    const fetchFn = deps.fetch ?? fetch;
    const log = deps.log ?? consoleLog;
    const today = (deps.now ?? (() => new Date()))();
    const now = today.getTime();
    const store = new ArchiveStore(deps.storage, username);
    // A click is refused within a minute of the last, and the click time is
    // kept with the rest of the bookkeeping so a reload does not reset it.
    // The user is told how long is left, in a toast. The daily sync is not a
    // click: the day rule governs it, so a reload right after a click still
    // syncs a new day.
    const before = await store.readState();
    const remaining = IMPORT_CLICK_INTERVAL_MS - ageMs(before.importClickedAt, today);
    if (!auto && remaining > 0) {
      deps.toast?.show(importWaitMessage(remaining), { kind: 'warning' });
      return;
    }
    const importClickedAt = auto ? before.importClickedAt : today.toISOString();
    await store.writeState({ ...before, importClickedAt });
    set({ ...get(), importClickedAt });
    /** The import completed: today is synced, and the time noted, in the bookkeeping and in the state. */
    const synced = async (): Promise<Pick<State, 'imported' | 'lastSyncDay' | 'lastImportAt'>> => {
      const state = await store.readState();
      const lastSyncDay = localDay(today);
      const lastImportAt = today.toISOString();
      await store.writeState({ ...state, lastSyncDay, lastImportAt });
      return { imported: state.months, lastSyncDay, lastImportAt };
    };
    set({ status: 'importing', progress: undefined, error: undefined, note: undefined, latest: undefined });
    try {
      const archives = await loadArchives({ store, fetch: fetchFn, username, now: today, log, mayFetch: true });
      const months = archives ? monthsSince(archives, periodStart('30d', now)) : [];
      if (!months.length) {
        set({ status: 'done', archives, ...(await synced()), note: 'No games on chess.com in the last 30 days.' });
        return;
      }
      const progress: ImportProgress = { month: monthOf(months[0]), imported: 0, skipped: 0 };
      const importedAt = today.toISOString();
      let latest: { id: string; opponent: string; endTime: number } | undefined;
      for (const month of months) {
        const state = await store.readState();
        const closed = state.closedMonths ?? [];
        // The live month can still grow: fetched again at most once a minute.
        // A past month is fetched once after it ended (a copy stored while it
        // was live may miss its last days), then served from storage for good.
        const live = monthOf(month) === currentMonth(today);
        const fresh = live ? ageMs(state.liveMonthFetchedAt, today) >= LIVE_MONTH_INTERVAL_MS : !closed.includes(month);
        const games = await loadMonth({ store, fetch: fetchFn, url: month, log, fresh });
        const after = await store.readState();
        await store.writeState({
          ...after,
          liveMonthFetchedAt: live && fresh ? today.toISOString() : after.liveMonthFetchedAt,
          closedMonths: !live && !closed.includes(month) ? [...closed, month] : after.closedMonths,
          months: after.months.includes(month) ? after.months : [...after.months, month],
        });
        progress.month = monthOf(month);
        set({ progress: { ...progress } });
        for (const game of games) {
          // Only the window's games: nothing older than 30 days comes in.
          if (!game.end_time || !inPeriod(game.end_time * 1000, '30d', now)) continue;
          const record = toGameRecord(game, username, importedAt);
          if (await deps.games.get(record.id)) progress.skipped++;
          else {
            await deps.games.put(record);
            progress.imported++;
            if (!latest || game.end_time > latest.endTime) {
              const mine = record.white.name.toLowerCase() === username.toLowerCase();
              latest = { id: record.id, opponent: (mine ? record.black : record.white).name, endTime: game.end_time };
            }
          }
          set({ progress: { ...progress } });
        }
      }
      set({ status: 'done', archives, ...(await synced()), latest: latest && { id: latest.id, opponent: latest.opponent } });
    } catch (e) {
      set({ status: 'error', error: e instanceof Error ? e.message : String(e) });
    }
  }

  return {
    username: '',
    status: 'loading',
    imported: [],
    // Reads storage only; nothing is fetched on load.
    async load(settings, archives) {
      const username = readUsername(await settings.readText(SETTINGS_FILE));
      const store = username ? new ArchiveStore(archives, username) : undefined;
      const state = store ? await store.readState() : undefined;
      set({
        username,
        archives: store ? await store.readArchives() : undefined,
        imported: state?.months ?? [],
        lastSyncDay: state?.lastSyncDay,
        lastImportAt: state?.lastImportAt,
        importClickedAt: state?.importClickedAt,
        status: get().status === 'loading' ? 'idle' : get().status,
      });
    },
    importRecent: (deps, options) => run(deps, !!options?.auto),
  };
});

/** Whether a button may be pressed now: an account is set and nothing is running. */
export function canImport(state: Pick<State, 'username' | 'status'>): boolean {
  return !!state.username && state.status !== 'loading' && state.status !== 'importing';
}

/** "just now", "3 min ago", "2 h ago", "yesterday", "3 days ago": how long since a moment. */
export function agoText(ms: number): string {
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

/** Milliseconds the Sync button still waits after the last click (a minute: chess.com's live month is read at most once a minute); 0 when it may be pressed. */
export function coolingMs(state: Pick<State, 'importClickedAt'>, now: Date): number {
  return Math.max(0, IMPORT_CLICK_INTERVAL_MS - ageMs(state.importClickedAt, now));
}

/** The one-line status beside the button; `now` for how long ago the last sync was. */
export function statusText(state: Pick<State, 'username' | 'status' | 'progress' | 'error' | 'note' | 'lastImportAt'>, now = new Date()): string {
  const p = state.progress;
  switch (state.status) {
    case 'loading':
      return '';
    case 'idle':
      // The line says when the last sync was, so the reader knows whether to press (2026-10-14).
      if (!state.username) return 'Set your chess.com username in Settings to import.';
      return state.lastImportAt ? `Synced ${agoText(ageMs(state.lastImportAt, now))}` : '';
    case 'importing':
      return p ? `Importing the last 30 days, ${p.imported} games so far.` : 'Starting import.';
    case 'done': {
      if (state.note) return state.note;
      const n = p?.imported ?? 0;
      const skipped = p?.skipped ? `, ${p.skipped} already in the library` : '';
      return `Imported ${n} ${n === 1 ? 'game' : 'games'} from the last 30 days${skipped}.`;
    }
    case 'error':
      return `Import failed: ${state.error ?? 'unknown error'}`;
  }
}

/** "Import available again in 42 s", for a click refused inside the minute. */
export function importWaitMessage(remainingMs: number): string {
  const seconds = Math.max(1, Math.ceil(remainingMs / 1000));
  return `Import available again in ${seconds} s`;
}

/**
 * Whether chess.com should be read again for new games now, without a click
 * (2026-10-14): an account is set, nothing is running, and the last import
 * is older than REFRESH_INTERVAL_MS (never, or ten minutes ago). The daily
 * sync's own case (no import today) is the sync screen's; this is the
 * in-between: a reload or a return to the tab after a while, so a game
 * played meanwhile comes in and, being the newest, is scanned first.
 */
export function refreshDue(state: Pick<State, 'username' | 'status' | 'lastImportAt'>, now: Date): boolean {
  if (!state.username || state.status === 'loading' || state.status === 'importing') return false;
  return ageMs(state.lastImportAt, now) >= REFRESH_INTERVAL_MS;
}
